import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects with title and key
 * @param {String} filename - Name of the file to download
 */
export const exportToCSV = (data, headers, filename = 'export.csv') => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    // Create CSV header row
    const csvHeaders = headers.map(header => header.title).join(',');

    // Create CSV data rows
    const csvRows = data.map(row => {
        return headers.map(header => {
            let value = row[header.key];

            // Handle nested objects
            if (header.key.includes('.')) {
                const keys = header.key.split('.');
                value = keys.reduce((obj, key) => obj?.[key], row);
            }

            // Handle null/undefined
            if (value === null || value === undefined) {
                value = '';
            }

            // Handle objects and arrays
            if (typeof value === 'object') {
                value = JSON.stringify(value);
            }

            // Handle values with commas, quotes, or newlines
            value = String(value);
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                value = `"${value.replace(/"/g, '""')}"`;
            }

            return value;
        }).join(',');
    });

    // Combine headers and rows
    const csvContent = [csvHeaders, ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Export data to PDF format
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects with title and key
 * @param {String} filename - Name of the file to download
 * @param {String} title - Title of the PDF document
 */
export const exportToPDF = (data, headers, filename = 'export.pdf', title = 'Data Export') => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    try {
        // Create new PDF document with A3 landscape for more space
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a3' // Larger format for more columns
        });

        // Add title
        doc.setFontSize(16);
        doc.text(title, 14, 15);

        // Add date and record count
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()} | Total Records: ${data.length}`, 14, 22);

        // Prepare table headers
        const tableHeaders = headers.map(header => header.title);

        // Prepare table data
        const tableData = data.map(row => {
            return headers.map(header => {
                let value = row[header.key];

                // Handle nested objects
                if (header.key && header.key.includes('.')) {
                    const keys = header.key.split('.');
                    value = keys.reduce((obj, key) => obj?.[key], row);
                }

                // Handle null/undefined
                if (value === null || value === undefined) {
                    return '';
                }

                // Handle objects and arrays
                if (typeof value === 'object') {
                    return JSON.stringify(value);
                }

                return String(value);
            });
        });

        // Add table to PDF using autoTable
        autoTable(doc, {
            head: [tableHeaders],
            body: tableData,
            startY: 28,
            styles: {
                fontSize: 6, // Smaller font
                cellPadding: 1.5, // Reduced padding
                overflow: 'linebreak',
                cellWidth: 'wrap',
                minCellHeight: 8
            },
            headStyles: {
                fillColor: [59, 130, 246], // Blue color
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 7,
                cellPadding: 2
            },
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: { top: 10, right: 10, bottom: 10, left: 10 },
            tableWidth: 'auto',
            columnStyles: {
                // Adjust column widths based on content
                0: { cellWidth: 12 }, // S.No
                1: { cellWidth: 25 }, // Bill Number
                2: { cellWidth: 30 }, // Transaction ID
                3: { cellWidth: 20 }, // Date
                4: { cellWidth: 20 }, // Client Name
                5: { cellWidth: 20 }, // Phone Number
                6: { cellWidth: 15 }, // Gender
                7: { cellWidth: 20 }, // Bill Type
                8: { cellWidth: 15 }, // Services Count
                9: { cellWidth: 15 }, // Products Count
                10: { cellWidth: 15 }, // Memberships Count
                11: { cellWidth: 20 }, // Total Amount
                12: { cellWidth: 18 }, // Discount
                13: { cellWidth: 18 }, // GST Amount
                14: { cellWidth: 15 }, // Payment Status
                15: { cellWidth: 25 }, // Payment Methods
                16: { cellWidth: 20 }, // Total Paid
                17: { cellWidth: 15 }, // Status
                18: { cellWidth: 18 }, // Coupon Code
                19: { cellWidth: 18 }, // Coupon Discount
                20: { cellWidth: 15 }  // Time
            }
        });

        // Save PDF
        doc.save(filename);
    } catch (error) {
        console.error('PDF Export Error:', error);
        alert('Error generating PDF. Please try CSV export instead.');
    }
};

/**
 * Get exportable headers from table headers
 * Filters out action columns and prepares headers for export
 * @param {Array} tableHeaders - Array of table header objects
 * @returns {Array} - Filtered headers suitable for export
 */
export const getExportableHeaders = (tableHeaders) => {
    return tableHeaders.filter(header => {
        // Exclude action columns and serial number columns
        const excludeKeys = ['actions', 'serialNumber'];
        return !excludeKeys.includes(header.key);
    });
};
